"use client";

import { useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";

import {
  broadcastChannel,
  type BroadcastMessage,
} from "@/lib/broadcast-channel";
import { clearCookiesClientSide } from "@/lib/cookies.client";

/**
 * Hook for handling cross-tab communication
 * Automatically handles sign-out events from other tabs
 */
export function useBroadcastChannel() {
  const router = useRouter();

  const handleSignOut = useCallback(
    async (message: BroadcastMessage) => {
      console.log("Received sign-out broadcast:", message);

      // Clear client-side cookies immediately
      clearCookiesClientSide([
        "_tact",
        "authjs.callback-url",
        "__Secure-authjs.callback-url",
        "authjs.session-token",
        "__Secure-authjs.session-token",
        "net.session-token",
        "net.refresh-token",
      ]);

      // Clear session storage
      try {
        sessionStorage.clear();
        localStorage.removeItem("org-tree-cache");
      } catch (error) {
        console.error("Error clearing storage:", error);
      }

      // Force immediate redirect using window.location for better reliability
      if (typeof window !== "undefined") {
        window.location.replace("/auth/sign-in");
      } else {
        router.replace("/auth/sign-in");
      }
    },
    [router],
  );

  const handleSignIn = useCallback((message: BroadcastMessage) => {
    console.log("Received sign-in broadcast:", message);
    // Optionally refresh the page or update UI
    // window.location.reload();
  }, []);

  const handleSessionUpdate = useCallback((message: BroadcastMessage) => {
    console.log("Received session update broadcast:", message);
    // Handle session updates if needed
  }, []);

  useEffect(() => {
    // Check for any stored sign-out events on mount
    const storedEvent = broadcastChannel.checkStoredSignOutEvent();
    if (storedEvent && storedEvent.type === "SIGN_OUT") {
      console.log("Found stored sign-out event on mount, processing...");
      handleSignOut(storedEvent);
      return; // Don't set up listeners if we're signing out
    }

    // Set up listeners for different message types
    const unsubscribeSignOut = broadcastChannel.addListener(
      "SIGN_OUT",
      handleSignOut,
    );
    const unsubscribeSignIn = broadcastChannel.addListener(
      "SIGN_IN",
      handleSignIn,
    );
    const unsubscribeSessionUpdate = broadcastChannel.addListener(
      "SESSION_UPDATE",
      handleSessionUpdate,
    );

    // Handle page visibility changes to ensure messages are processed
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // When tab becomes visible, check for any pending sign-out events
        // This helps catch messages that might have been missed
        console.log("Tab became visible, checking for pending events");

        const storedEvent = broadcastChannel.checkStoredSignOutEvent();
        if (storedEvent && storedEvent.type === "SIGN_OUT") {
          console.log("Found stored sign-out event, processing...");
          handleSignOut(storedEvent);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup listeners on unmount
    return () => {
      unsubscribeSignOut();
      unsubscribeSignIn();
      unsubscribeSessionUpdate();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleSignOut, handleSignIn, handleSessionUpdate]);

  return {
    broadcastSignOut: broadcastChannel.broadcastSignOut,
    broadcastSignIn: broadcastChannel.broadcastSignIn,
    broadcastSessionUpdate: broadcastChannel.broadcastSessionUpdate,
  };
}
